import { CommonModule } from '@angular/common';
import { Component, OnInit, OnDestroy, inject, signal, input, computed, effect, ElementRef, ViewChild, ViewContainerRef, HostListener, untracked, ChangeDetectionStrategy } from '@angular/core';
import { ContentRatingGate } from '@core/content-ratings';
import { SettingsStore } from '@core/settings';
import { SkinningService } from '@core/skin';
import { TranslocoPipe } from '@jsverse/transloco';
import { ContentRatingLock } from '@shared/components/content-rating-lock/content-rating-lock';
import { TranscodeImageDirective } from '@shared/directives/transcode-image.directive';
import { RoundedIconButton } from 'app/shared/components/rounded-icon-button/rounded-icon-button';
import { ItemAssetType, PlayerTypes } from 'app/features/player/data-access/player.config';
import { PlayerService } from 'app/features/player/data-access/player.service';
import { ThrottleClickDirective } from 'app/shared/directives/throttleClick.directive';
import { clpp } from '@castlabs/prestoplay';
import { PlayerItem } from 'app/features/player/data-access/player.model';
import { DetailsService } from '@features/details/data-access/details.service';
import { AssetSearchService } from '@features/assets/data-access';
import { BadgeComponent } from 'app/shared/components/badge/badge.component';
import { BadgeVariant } from 'app/shared/components/badge/badge.model';
import { HERO_TRAILER_VISIBILITY_THRESHOLD } from '../hero-slider.config';

@Component({
    selector: 'hero-slider',
    templateUrl: './hero-slider.component.html',
    styleUrls: ['./hero-slider.component.scss'],
    imports: [
        CommonModule,
        TranscodeImageDirective,
        RoundedIconButton,
        TranslocoPipe,
        ThrottleClickDirective,
        BadgeComponent,
        ContentRatingLock,
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HeroSliderComponent implements OnInit, OnDestroy {
    public settings = inject(SettingsStore);
    public skinning = inject(SkinningService);
    protected gate = inject(ContentRatingGate);
    private playerService = inject(PlayerService);
    private detailsService = inject(DetailsService);
    private searchService = inject(AssetSearchService);
    private host: ElementRef<HTMLElement> = inject(ElementRef);

    public catalogue = input<any>();

    public BadgeVariant = BadgeVariant;
    public displayIndex = signal(1);
    public currentIndex = computed(() => {
        const originalItems = this.items();
        if (originalItems.length === 0) return 0;
        return (this.displayIndex() - 1 + originalItems.length) % originalItems.length;
    });
    public isTransitioning = signal(true);
    public isSliding = signal(false);
    public animateContent = signal(true);
    public items = computed(() => this.catalogue()?.items || []);
    public itemsWithClones = computed(() => {
        const originalItems = this.items();
        if (originalItems.length === 0) return [];
        // [Clone of Last, ...Originals, Clone of First]
        return [originalItems[originalItems.length - 1], ...originalItems, originalItems[0]];
    });
    public item = computed(() => {
        const originalItems = this.items();
        if (originalItems.length === 0) return null;
        let idx = this.displayIndex() - 1;
        if (idx < 0) idx = originalItems.length - 1;
        if (idx >= originalItems.length) idx = 0;
        return originalItems[idx];
    });
    public playerMuted = this.playerService.isPlayerMuted();
    public disablePlayButton = signal(false);
    public showHeroDescription = this.settings.showHeroDescription;
    /**
     * The badge's live "has it started yet" verdict, tagged with the slide it was emitted for. One
     * shared boolean leaked across slides: an upcoming slide set it to false, and a later slide whose
     * badge never emits (no badge, or nothing left to count down) inherited that false — and lost
     * its play button.
     */
    private availabilityStartPassed = signal<{ item: unknown; passed: boolean } | null>(null);

    public showBadge = computed(() => {
        const currentItem = this.item();
        if (!currentItem) return false;

        if (this.catalogue()?.hideBadge === true) return false;

        const hasRegularBadge = currentItem.badge !== null && currentItem.badge !== undefined;
        const hasOutgoing = currentItem.hasOutgoingEnabled && !!currentItem.availabilityEndTime;
        const hasUpcoming = currentItem.hasUpcomingEnabled && !!currentItem.availabilityStartTime;

        return hasRegularBadge || hasOutgoing || hasUpcoming;
    });

    // Automatic "new episode" badge applies only to episode-type cover items
    public isAutoBadgeEpisode = computed(() => this.item()?.type === ItemAssetType.Episode);

    showPlayButton = computed(() => {
        const item = this.item();
        const now = new Date().getTime();
        if (!item) return false;
        // Parental gate (AVIION-36952): no play — and no trailer preview, see previewVideo().
        if (this.gate.isRestricted(item)) return false;

        if (item.timeStart && new Date(item.timeStart).getTime() > now) {
            return false;
        }

        if (!item.availabilityStartTime) {
            return true;
        }

        const fromBadge = this.availabilityStartPassed();
        if (fromBadge && fromBadge.item === item) {
            return fromBadge.passed;
        }

        // Fallback for initial load before badge emits:
        const startTime = new Date(item.availabilityStartTime).getTime();


        return startTime <= now;
    });

    onAvailabilityStartPassed(passed: boolean) {
        this.availabilityStartPassed.set({ item: this.item(), passed });
    }

    private state = this.playerService.getState();
    @ViewChild('playerContainer', { read: ViewContainerRef, static: false }) container!: ViewContainerRef;

    private playerItem = computed(() => {
        // V2 DTO carries the trailer URL in `trailerFileName`; the player still reads `previewVideoUrl`.
        const previewVideoUrl = (this.item() as { previewVideoUrl?: string } | null)?.previewVideoUrl
            ?? (this.item() as { trailerFileName?: string } | null)?.trailerFileName ?? undefined;
        // ng_source/ng_contentType are already tagged on the cover item at the API boundary.
        return { ...this.item(), previewVideoUrl } as PlayerItem;
    });

    public previewVideoPlaying = computed(() => {
        const state = this.playerService.getState()();
        const playerLayoutType = this.playerService.getPlayerLayoutType()();
        return state == clpp.Player.State.PLAYING && playerLayoutType == PlayerTypes.Cover
    })

    private intervalId: any;
    private previewTimeoutId: any;
    private autoSlideSpeed = 7500; // 7.5 seconds
    private autoPlayDelay = 2000; // 2 seconds
    public isFocused = signal(false);

    private visibilityObserver?: IntersectionObserver;
    /** Set only when *we* paused a running preview, so scrolling back never starts one nobody asked for. */
    private pausedByScroll = false;

    constructor() {
        effect(() => {
            const state = this.state();
            untracked(() => {
                const playerLayoutType = this.playerService.getPlayerLayoutType()();
                if (state == clpp.Player.State.ENDED && playerLayoutType == PlayerTypes.Cover) {
                    this.startSlideShow();
                }
            });
        });
        effect(() => {
            const items = this.items();
            if (items.length > 0) {
                untracked(() => {
                    this.displayIndex.set(1);
                    this.isTransitioning.set(true);
                });
            }
        });

        effect(() => {
            // The search overlay only hides the page (display:none) instead of destroying it, so the
            // trailer would keep playing — audibly — behind the results.
            const searching = this.searchService.isVisible();
            untracked(() => {
                if (searching) {
                    this.stopSlideShow();
                    this.resetPreview();
                } else {
                    this.startSlideShow();
                }
            });
        });

        effect(() => {
            // Track the slide index, not just the item: navigating back to a slide hands back the
            // same object, and a computed that re-emits an identical reference notifies nobody — so
            // the trailer that played on the first pass never started again (AVIION-36265).
            this.currentIndex();
            const item = this.item();
            if (this.hasTrailer(item)) {
                untracked(() => {
                    const playerLayoutType = this.playerService.getPlayerLayoutType()();
                    if (playerLayoutType == PlayerTypes.Cover || playerLayoutType == PlayerTypes.None) {
                        this.previewVideo();
                    }
                });
            }
        });
    }

    ngOnInit() {
        this.startSlideShow();
        this.observeHeroVisibility();
    }

    ngOnDestroy() {
        this.stopSlideShow();
        this.visibilityObserver?.disconnect();
        // The player is shared and outlives this component: leaving the page without releasing the
        // preview leaves the trailer audible on whatever screen comes next.
        this.resetPreview();
    }

    /**
     * Scrolling down to the content below used to leave the trailer running — and audible — behind
     * it. Pause once the hero is less than `HERO_TRAILER_VISIBILITY_THRESHOLD` on screen, and pick
     * it back up where it left off when the user scrolls back.
     */
    private observeHeroVisibility() {
        if (typeof IntersectionObserver === 'undefined') {
            return;
        }
        this.visibilityObserver = new IntersectionObserver(
            entries => {
                const entry = entries[entries.length - 1];
                if (!entry) return;
                if (entry.intersectionRatio >= HERO_TRAILER_VISIBILITY_THRESHOLD) {
                    this.resumePreviewAfterScroll();
                } else {
                    this.pausePreviewForScroll();
                }
            },
            // Both edges of the rule, so the callback fires crossing the threshold in either direction.
            { threshold: [0, HERO_TRAILER_VISIBILITY_THRESHOLD] },
        );
        this.visibilityObserver.observe(this.host.nativeElement);
    }

    private pausePreviewForScroll() {
        // previewVideoPlaying() is already scoped to a PLAYING cover preview, so a full-screen
        // player or a details trailer that took the shared player over is left alone.
        if (!this.previewVideoPlaying()) {
            return;
        }
        this.pausedByScroll = true;
        this.playerService.pause();
        // Keep the carousel still too. A slide change runs resetSlide() -> resetPreview(), which
        // closes the shared player outright — scrolling back would then find nothing to resume.
        this.stopSlideShow();
    }

    private resumePreviewAfterScroll() {
        if (!this.pausedByScroll) {
            return;
        }
        this.pausedByScroll = false;
        // Something else may have claimed the shared player while we were scrolled away (details
        // modal, full-screen playback, the search overlay closing the preview) — only resume ours.
        if (this.playerService.getPlayerLayoutType()() !== PlayerTypes.Cover) {
            return;
        }
        this.playerService.play();
        // startSlideShow() bails out while a preview is playing, but a mouseenter landing during
        // the paused window gets past that guard — drop the interval it armed, or the trailer we
        // just resumed is cut off at the next tick.
        this.stopSlideShow();
    }

    @HostListener('mouseenter')
    coverFocused() {
        this.startSlideShow();
        this.isFocused.set(true);
        // previewVideo() starts the trailer only if the pointer is over the hero when its start
        // delay runs out. A pointer arriving later found nothing armed, and the slide stayed silent
        // until it changed. Arm it now when this slide's trailer never started: None means nothing
        // is loaded; Cover (playing, ended, or paused by scroll) is left alone.
        if (this.playerService.getPlayerLayoutType()() === PlayerTypes.None && this.hasTrailer(this.item())) {
            this.previewVideo();
        }
    }

    private hasTrailer(item: { previewVideoUrl?: string; trailerFileName?: string } | null): boolean {
        return !!(item?.previewVideoUrl || item?.trailerFileName);
    }

    @HostListener('mouseleave')
    coverUnfocused() {
        this.isFocused.set(false);
    }

    startSlideShow() {
        this.stopSlideShow();
        if (this.previewVideoPlaying()) {
            return;
        }
        this.intervalId = setInterval(() => {
            this.nextSlide();
        }, this.autoSlideSpeed);
    }

    stopSlideShow() {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }
    }

    prevSlide() {
        if (this.isSliding()) return;
        this.resetSlide();
        if (!this.isTransitioning()) return;
        this.isSliding.set(true);
        this.displayIndex.update(i => i - 1);
        this.triggerContentAnimation();
    }

    nextSlide() {
        if (this.isSliding()) return;
        this.resetSlide();
        if (!this.isTransitioning()) return;
        this.isSliding.set(true);
        this.displayIndex.update(i => i + 1);
        this.triggerContentAnimation();
    }

    private triggerContentAnimation() {
        this.animateContent.set(false);
        setTimeout(() => this.animateContent.set(true), 850);
    }

    handleTransitionEnd() {
        this.isSliding.set(false);
        const totalWithClones = this.itemsWithClones().length;
        if (this.displayIndex() === 0) {
            // Reached clone of last item at the beginning, jump to actual last item
            this.isTransitioning.set(false);
            this.displayIndex.set(totalWithClones - 2);
            setTimeout(() => this.isTransitioning.set(true));
        } else if (this.displayIndex() === totalWithClones - 1) {
            // Reached clone of first item at the end, jump to actual first item
            this.isTransitioning.set(false);
            this.displayIndex.set(1);
            setTimeout(() => this.isTransitioning.set(true));
        }
    }

    resetSlide() {
        this.stopSlideShow();
        this.startSlideShow();
        this.resetPreview();
    }

    resetPreview() {
        clearTimeout(this.previewTimeoutId);
        // Any preview owned by the cover, not only one that already reached PLAYING: a load still in
        // flight would otherwise surface over the next slide. close() also drops the player's item and
        // layout, so returning to this slide is not swallowed by playItem's duplicate guard.
        if (this.playerService.getPlayerLayoutType()() === PlayerTypes.Cover) {
            this.playerService.close();
        }
    }

    togglePreviewVideoMute() {
        this.playerService.toggleMute();
    }

    public openDetails() {
        const item = this.item();
        this.detailsService.open({ item });
    }

    previewVideo() {
        if (!this.playerService.isPreviewAutoplayEnabled() || this.gate.isRestricted(this.item())) {
            return;
        }
        this.stopSlideShow();
        // Paging through slides queues one delayed start per slide; only the slide we settled on
        // should actually play.
        clearTimeout(this.previewTimeoutId);
        this.previewTimeoutId = setTimeout(() => {
            if (!this.isFocused()) {
                return;
            }
            this.playerService.changePlayerViewLocation(this.container);
            this.playerService.playItem(this.playerItem(), PlayerTypes.Cover);
        }, this.autoPlayDelay);
    }

    onPlay() {
        if (!this.showPlayButton()) {
            return;
        }

        this.resetPreview();
        this.playerService.changePlayerViewLocation();
        this.playerService.playItem(this.playerItem(), PlayerTypes.Normal);
    }

    onDot(index: number) {
        if (this.isSliding() || index === this.currentIndex()) return;
        this.resetPreview();
        this.stopSlideShow();
        this.isSliding.set(true);
        this.isTransitioning.set(true);
        this.displayIndex.set(index + 1);
        this.triggerContentAnimation();
        this.startSlideShow();
    }
}
